// =====================================================
// NOTIFICATION PERMISSION — the browser's real state, nothing else
//
// The single place the app reads, requests and watches notification
// permission. It never stores or invents a "granted" flag: every answer comes
// from the browser at the moment it is asked, which is what makes a permission
// revoked in Settings show up on the next check.
//
//   readPermission()      "granted" | "denied" | "default" | "unsupported"
//   requestPermission()   asks the browser (must run from a user gesture)
//   watchPermission(cb)   calls cb whenever the state may have changed
//   settingsHelp()        how to re-enable on the current platform
//
// What the platform does NOT tell a website — and this does not pretend to:
// an uninstall, or a change made while the app is closed. Those are picked up
// the next time the app is opened, because the state is read fresh every time.
// =====================================================

export function readPermission() {
  try {
    if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
    const p = window.Notification.permission;
    return p === "granted" || p === "denied" ? p : "default";
  } catch {
    return "unsupported";
  }
}

// Ask the browser. Resolves to the state AFTER the prompt — a dismissed prompt
// resolves to "default", never to "granted". Handles old Safari's
// callback-only form.
export async function requestPermission() {
  if (readPermission() === "unsupported") return "unsupported";
  try {
    const result = await new Promise((resolve) => {
      const maybe = window.Notification.requestPermission(resolve);
      if (maybe && typeof maybe.then === "function") maybe.then(resolve, () => resolve(readPermission()));
    });
    return result === "granted" || result === "denied" ? result : readPermission();
  } catch {
    return readPermission();
  }
}

// Re-check whenever the permission may have changed: the Permissions API
// change event where it exists, plus page focus / visibility (the moment a
// user comes back from the Settings screen), plus a light poll as a fallback
// for browsers that fire neither. Returns an unsubscribe function.
export function watchPermission(callback, { pollMs = 10000 } = {}) {
  let last = readPermission();
  const check = () => {
    const now = readPermission();
    if (now !== last) {
      last = now;
      callback(now);
    }
  };

  let status = null;
  const onStatusChange = () => check();
  try {
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: "notifications" })
        .then((s) => {
          status = s;
          if (s.addEventListener) s.addEventListener("change", onStatusChange);
          else s.onchange = onStatusChange;
        })
        .catch(() => {});
    }
  } catch {
    /* Permissions API unavailable — focus/visibility/poll still cover it */
  }

  const onVisibility = () => document.visibilityState === "visible" && check();
  window.addEventListener("focus", check);
  document.addEventListener("visibilitychange", onVisibility);
  const timer = setInterval(() => document.visibilityState === "visible" && check(), pollMs);

  return () => {
    window.removeEventListener("focus", check);
    document.removeEventListener("visibilitychange", onVisibility);
    clearInterval(timer);
    if (status) {
      if (status.removeEventListener) status.removeEventListener("change", onStatusChange);
      else status.onchange = null;
    }
  };
}

function isStandalone() {
  try {
    return (
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true
    );
  } catch {
    return false;
  }
}

// Platform-specific instructions for re-enabling notifications once the
// browser has stopped showing the prompt (after "Block", most browsers never
// show it again — only the user can undo that, from Settings).
export function settingsHelp() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const ios = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const android = /Android/i.test(ua);
  const firefox = /Firefox\//.test(ua);
  const edge = /Edg\//.test(ua);
  const safari = /Safari\//.test(ua) && !/Chrome\/|Chromium\/|Edg\//.test(ua);

  if (ios) {
    return isStandalone()
      ? {
          platform: "iPhone / iPad",
          steps: [
            "Open the Settings app.",
            "Scroll down and tap Notifications.",
            "Find TheBlogsSphere and turn on Allow Notifications.",
            "Come back to the app — it checks again automatically.",
          ],
        }
      : {
          platform: "iPhone / iPad",
          steps: [
            "Safari only allows notifications for apps added to the Home Screen (iOS 16.4 or later).",
            "Tap the Share button, then Add to Home Screen.",
            "Open TheBlogsSphere from your Home Screen and allow notifications when asked.",
          ],
        };
  }
  if (android) {
    return {
      platform: "Android",
      steps: [
        "Tap the lock / tune icon next to the address bar (or open the app's info page if installed).",
        "Tap Permissions → Notifications and choose Allow.",
        "Or: Android Settings → Apps → your browser / TheBlogsSphere → Notifications → On.",
        "Come back to the app — it checks again automatically.",
      ],
    };
  }
  if (firefox) {
    return {
      platform: "Firefox",
      steps: [
        "Click the permissions icon to the left of the address bar.",
        "Next to Send Notifications, remove the Blocked setting.",
        "Reload the page and press Allow notifications again.",
      ],
    };
  }
  if (safari) {
    return {
      platform: "Safari (Mac)",
      steps: [
        "Open Safari → Settings → Websites → Notifications.",
        "Set this site to Allow.",
        "Come back to this tab — it checks again automatically.",
      ],
    };
  }
  return {
    platform: edge ? "Microsoft Edge" : "Chrome",
    steps: [
      "Click the icon to the left of the address bar (a lock or tune icon).",
      "Set Notifications to Allow (or open Site settings → Notifications → Allow).",
      "Come back to this tab — it checks again automatically.",
    ],
  };
}
